import * as React from 'react';
import { useAtomValue } from 'jotai';
import { useAtomCallback } from 'jotai/utils';
import { STALE_DATA_TIMEOUT } from '@studio/constants/api-constants';
import { useClient } from '@studio/hooks/use-client';
import { runCachedAbortableStoreFetch } from '@studio/storeV2/helpers/run-cached-abortable-store-fetch';
import type { Regions } from '@studio/types';
import { regionsAtom, regionsLoadInFlightAtom } from '../../regions';

type _Props = {
  fetchListOnMount?: boolean;
};

const DEFAULT_REGION: Regions.Get.Region = {
  uuid: '1290ae83-b319-4cf4-9069-41a7296c8c2c',
  name: 'North America',
  zone: 'us',
  defaultRegion: true,
  restricted: false,
  latencyCheckUrl: 'https://stream-export.resi.io/api/v1/ping',
  hotspotsServiceUrl: 'https://hotspots.resi.io',
};

const useRegions = ({ fetchListOnMount = false }: _Props = {}) => {
  const regionsState = useAtomValue(regionsAtom);
  const regionsLoadInFlight = useAtomValue(regionsLoadInFlightAtom);

  const { callApi: getRegions, isFetching: isClientFetching } = useClient({
    config: useClient.central.v3.regions.GET,
  });

  const fetchRegions = useAtomCallback(
    React.useCallback(
      (get, set, options?: { forceRefresh?: boolean }) => {
        const { forceRefresh } = options ?? {};
        return runCachedAbortableStoreFetch(get, set, {
          dataAtom: regionsAtom,
          emptyOnError: [],
          fetchWithSignal: (signal) => getRegions(undefined, { signal }),
          forceRefresh,
          loadInFlightAtom: regionsLoadInFlightAtom,
          staleTimeoutMs: STALE_DATA_TIMEOUT,
        });
      },
      [getRegions]
    )
  );

  React.useEffect(() => {
    if (!fetchListOnMount) {
      return;
    }
    void fetchRegions();
  }, [fetchListOnMount, fetchRegions]);

  const isFetching = regionsLoadInFlight != null || isClientFetching;

  const defaultRegion = React.useMemo<Regions.Get.Region>(
    () => regionsState.data.find((region) => region.defaultRegion) ?? DEFAULT_REGION,
    [regionsState.data]
  );

  return {
    defaultRegion,
    fetchRegions,
    hasFetched: regionsState.hasFetched,
    isFetching,
    regions: regionsState.data as Regions.Get.Region[],
  };
};

export default useRegions;
